import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import './styles/App.scss';
import { AppToaster } from 'shared/ui/app-toaster/app-toaster';
import Layout from 'components/layout/layout';
import { Suspense, useEffect, useRef, useState, ReactNode } from 'react';
import { BusinessProfileLazy } from 'pages/business-profile/business-profile.lazy';
import { BusinessProfileIndexLazy } from 'pages/business-profile/pages/index/index.lazy';
import { ServicePageLazy } from 'pages/business-profile/pages/service/service-page.lazy';
import {
  useBusinessInsights,
  useConversationalWritingRule,
  useStatisticalEvidenceRule,
  useTranslation,
  useVisibilityScore,
} from 'hooks';
import { nameToRoute } from 'shared/helpers/nameToRoute';
import { useSelectedProject } from 'hooks';
import { ApiErrorModal } from 'components/api-error-modal/api-error-modal';
import { PaymentPage } from 'pages/payment/payment-page';
import { useContentFreshnessRule } from 'hooks/api/useContentFreshnessRule';
import { ImprovementCenterPage } from 'pages/business-profile/pages/improvement-center/improvement-center';
import { PageViewTracker } from 'components/mixpanel/page-view-tracker';
import { UserSourceTracker } from 'components/mixpanel/user-source-tracker';
import { SessionReplay } from 'components/mixpanel/session-replay';
import { useTheme } from './styles/providers/theme-provider';
import { useRobotsTxtRule } from 'hooks/api/useRobotsTxtRule';
import { useLLmsRule } from 'hooks/api/useLLmsRule';
import { useSchemaRule } from 'hooks/api/useSchemaRule';
import { SubscriptionsPage } from 'pages/subscriptions/subscriotions-page';
import { AuthPage, AuthCallback, LogoutCallback } from 'pages/auth';
import { appRoute, ROUTE_PREFIX } from 'shared/constants/routes';
import { AddDomain } from 'pages/add-domain';
import {
  useGetUserTypeQuery,
  useSubmitLicenseMutation,
} from 'store/api/userApiSlice';
import { snackbar } from 'shared/helpers/snackbar';
import { userManager } from 'services/oidcConfig';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setShowBanner, setUser, setUserType } from 'store/slices/userSlice';
import { OfferingsPageLazy } from '../pages/business-profile/pages/offerings/offerings.lazy';
import { CreateOfferingPageLazy } from '../pages/business-profile/pages/offerings/create/create-offering.lazy';
import { EditOfferingPageLazy } from '../pages/business-profile/pages/offerings/edit/edit-offering.lazy';
import { captureTrafficSource } from 'utils/trafficSource';
import { identifyUser } from 'utils/mixpanel';

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';
const isLocal = process.env.REACT_APP_ENV === 'local';

const ConditionalRedirect = ({
  businessData: _businessData,
  businessLoading,
  isFetching,
  userinfoLoading,
}: {
  businessData: any;
  businessLoading: boolean;
  isFetching: boolean;
  userinfoLoading: boolean;
}) => {
  if (businessLoading || isFetching || userinfoLoading) {
    return <BusinessProfileLazy />;
  }

  return <Navigate to={appRoute('/dashboard')} replace />;
};

function AppShell() {
  const [submitLicense] = useSubmitLicenseMutation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user.user);
  const { data: userTypeData } = useGetUserTypeQuery();
  useEffect(() => {
    if (!user) {
      userManager.getUser().then(oidcUser => {
        if (oidcUser && !oidcUser.expired) {
          dispatch(
            setUser({
              email: (oidcUser.profile.email as string) || null,
              name: (oidcUser.profile.name as string) || null,
              sub: oidcUser.profile.sub || null,
            })
          );
        }
      });
    }
  }, [dispatch, user]);

  useEffect(() => {
    const licenceCode = localStorage.getItem('licenceCode');
    if (licenceCode && (user || isLocal)) {
      submitLicense({ code: licenceCode })
        .unwrap()
        .then(() => {
          snackbar(t('licenseActivated'));
          localStorage.removeItem('licenceCode');
          dispatch(setShowBanner(true));
        })
        .catch(e => {
          snackbar(t('licenseActivationFailed'));
          if (e.data?.message) {
            localStorage.removeItem('licenceCode');
          }
        });
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (userTypeData?.type) {
      dispatch(setUserType(userTypeData.type));
    }
  }, [dispatch, userTypeData]);
  const {
    showUserInfoErrorModal,
    handleUserInfoRetry,
    hideUserInfoErrorModal,
    isLoading: userinfoLoading,
    selectedProject,
  } = useSelectedProject();
  const {
    businessInsights,
    insightsLoading,
    insightsFetching,
    showInsightsErrorModal,
    handleInsightsRetry,
    hideInsightsErrorModal,
  } = useBusinessInsights();

  const { productBlocks, isLoading, isFetching } = useVisibilityScore();

  const lastKnownServicesRef = useRef(businessInsights?.services);
  if (businessInsights?.services) {
    lastKnownServicesRef.current = businessInsights.services;
  }
  const routableServices = lastKnownServicesRef.current;

  useConversationalWritingRule();
  useStatisticalEvidenceRule();
  useContentFreshnessRule();
  useRobotsTxtRule();
  useSchemaRule();
  useLLmsRule();

  if (
    selectedProject?.data?.userID &&
    localStorage.getItem('userId') !== selectedProject?.data?.userID
  ) {
    localStorage.setItem('userId', selectedProject?.data?.userID);
  }

  const mixpanelUserId = selectedProject?.data?.userID;
  useEffect(() => {
    if (mixpanelUserId) {
      identifyUser(mixpanelUserId);
    }
  }, [mixpanelUserId]);

  const { theme } = useTheme();
  const body = document.querySelector('body');
  if (body) {
    body.classList.add(`${theme}_theme`);
  }

  return (
    <>
      <div className='App'>
        <Layout>
          <Suspense fallback={<BusinessProfileLazy />}>
            <Routes>
              <Route
                path={ROUTE_PREFIX || '/'}
                element={<BusinessProfileLazy />}
              >
                <Route
                  index
                  element={<Navigate to={appRoute('/dashboard')} replace />}
                />
                <Route
                  path={appRoute('/dashboard')}
                  element={
                    <SessionReplay>
                      <BusinessProfileIndexLazy />
                    </SessionReplay>
                  }
                />
                <Route
                  path={appRoute('/improvement-center')}
                  element={
                    <SessionReplay>
                      <ImprovementCenterPage />
                    </SessionReplay>
                  }
                />
                <Route
                  path={appRoute('/offerings')}
                  element={
                    <SessionReplay>
                      <OfferingsPageLazy />
                    </SessionReplay>
                  }
                />
                {routableServices &&
                  routableServices.map((service, index) => (
                    <Route
                      key={service.name}
                      path={appRoute(
                        `/${nameToRoute(service.unique_id ?? service.name)}`
                      )}
                      element={<Outlet />}
                    >
                      <Route
                        index
                        element={
                          <ServicePageLazy
                            key={service.name}
                            serviceName={service.name}
                            serviceId={service.unique_id}
                            metricServices={productBlocks}
                            serviceNumber={index + 1}
                            metricsLoading={isLoading || isFetching}
                          />
                        }
                      />
                      <Route
                        path=':reportId'
                        element={
                          <ServicePageLazy
                            key={service.name}
                            serviceName={service.name}
                            serviceId={service.unique_id}
                            metricServices={productBlocks}
                            serviceNumber={index + 1}
                            metricsLoading={isLoading || isFetching}
                          />
                        }
                      />
                    </Route>
                  ))}
              </Route>

              {isStandalone ? (
                <>
                  <Route
                    path={appRoute('/subscriptions')}
                    element={
                      <SessionReplay>
                        <SubscriptionsPage />
                      </SessionReplay>
                    }
                  />
                  <Route
                    path={appRoute('/add-domain')}
                    element={
                      <SessionReplay>
                        <AddDomain />
                      </SessionReplay>
                    }
                  />
                </>
              ) : (
                <Route
                  path={appRoute('/payment', true)}
                  element={<PaymentPage />}
                />
              )}

              <Route
                path={appRoute('/offerings/create')}
                element={<CreateOfferingPageLazy />}
              />

              <Route
                path={appRoute('/offerings/edit')}
                element={<EditOfferingPageLazy />}
              />

              {/* Catch all other routes and redirect to business-profile after data loads */}
              <Route
                path='*'
                element={
                  <ConditionalRedirect
                    userinfoLoading={userinfoLoading}
                    businessData={businessInsights}
                    businessLoading={insightsLoading || isLoading}
                    isFetching={insightsFetching || isFetching}
                  />
                }
              />
            </Routes>
          </Suspense>
        </Layout>
        <AppToaster />
      </div>

      {/* API Error Modals */}
      <ApiErrorModal
        isOpen={showUserInfoErrorModal}
        onRetry={handleUserInfoRetry}
        onClose={hideUserInfoErrorModal}
      />
      <ApiErrorModal
        isOpen={showInsightsErrorModal}
        onRetry={handleInsightsRetry}
        onClose={hideInsightsErrorModal}
      />
    </>
  );
}

function AuthGuard({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    if (isLocal || !isStandalone) {
      setIsAuthenticated(true);
      return;
    }
    userManager.getUser().then(user => {
      setIsAuthenticated(!!user && !user.expired);
    });
  }, []);

  if (isAuthenticated === null) {
    return <BusinessProfileLazy />;
  }

  if (!isAuthenticated) {
    return <Navigate to='/auth' replace />;
  }

  return <>{children}</>;
}

function LicenceCodeCapture() {
  const location = useLocation();
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('activation_code');
    if (code) {
      localStorage.setItem('licenceCode', code);
    }
  }, [location.search]);
  return null;
}

function TrafficSourceCapture() {
  useEffect(() => {
    captureTrafficSource();
  }, []);
  return null;
}

function App() {
  return (
    <Router>
      <TrafficSourceCapture />
      <LicenceCodeCapture />
      <PageViewTracker />
      <UserSourceTracker />
      <Routes>
        {/* Auth routes — standalone, no Layout or data hooks */}
        <Route path='/auth' element={<AuthPage />} />
        <Route path='/auth/login-callback' element={<AuthCallback />} />
        <Route path='/auth/logout-callback' element={<LogoutCallback />} />

        {/* All other routes go through the main app shell */}

        <Route
          path='*'
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
